import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import {
  createGetSecurityIdentifierColumnQuickFilterText,
  EMPTY_ARRAY,
  getAgGridColId,
  getOrderStatusColor,
  getSecurityIdentifierColumns,
  isOrderRejected,
  Order,
  prettyPriceProtection,
  PricingModeEnum,
  SecurityToursEnum,
  useDefaultColumns,
  type AgGridMeterProps,
  type AgGridMeterTooltip,
  type Column,
  type ColumnDef,
  type FilledPercentColumnParams,
  type Leaves,
  type MeterWithValueProps,
  type PriceParams,
  type SizeColumnParams,
  type SubAccountColumnParams,
  type TextColumnParams,
} from '@talos/kyoko';
import type { CellClassParams, ValueFormatterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { DDHProgress } from '../../../components/DDH/DDHProgressTooltip';
import { MarketDistributionTooltipRenderer } from '../../../components/MarketDistribution/MarketDistributionTooltip';
import { shouldShowEntityUnifiedLiquidityMarketTooltip } from '../../../components/MarketSelector/entityUnifiedLiquidityMarketTooltipContent';
import { getUnifiedLiquidityMarketTooltipGroupsFromEntityMarkets } from '../../../components/MarketSelector/unifiedLiquidityMarketTooltip';
import { UnifiedLiquidityTooltipIndicator } from '../../../components/MarketSelector/UnifiedLiquidityTooltipIndicator';
import { OrderAnalyticsTooltipCellRenderer } from '../../../components/OrderAnalytics/OrderAnalyticsTooltip';
import { PredictionOutcomeTag } from '../../../components/PredictionMarkets/PredictionOutcomeTag';
import { useTradingSettings } from '../../../providers/TradingSettingsContext';
import { ORDER_COLUMN_IDS, type OrderBlotterDefaultColumn } from './orderColumnIds';

export const getMeterTooltip: AgGridMeterProps['tooltip'] = (
  props: { meterProps: MeterWithValueProps } & AgGridMeterProps
): AgGridMeterTooltip | null => {
  const data: unknown = props.data;
  if (!(data instanceof Order)) {
    return null;
  }

  if (data.isDDHParentOrder) {
    return {
      content: <DDHProgress order={data} meterProps={props.meterProps} />,
    };
  }

  if (!isOrderRejected(data.OrdStatus)) {
    return {
      type: 'customPopover',
      content: <MarketDistributionTooltipRenderer {...props} />,
    };
  }

  return null;
};

const getMeterClassName = (data: Order) => {
  if (data.isDDHParentOrder) {
    return 'ddh-order-meter';
  }
  return '';
};

const DEFAULT_ORDER_COLUMNS: Map<string, ColumnDef<Order>> = new Map(
  (
    [
      { field: ORDER_COLUMN_IDS.Warning, type: 'warning', params: { tooltipWidth: '400px' } },
      {
        field: ORDER_COLUMN_IDS.SubmitTime,
        type: 'date',
        sortable: true,
        initialSort: '-',
        title: 'Submit Date',
        params: { milliseconds: true },
        initialWidth: 140,
      },
      {
        field: ORDER_COLUMN_IDS.Symbol,
        type: 'security',
        sortable: true,
        initialWidth: 100,
        params: {
          // Returning nullish lets the security renderer fall back to its own suffix, e.g. the DDH icon.
          suffix: ({ data }) =>
            data instanceof Order && data.ExpressedOutcome != null ? (
              <PredictionOutcomeTag outcome={data.ExpressedOutcome} />
            ) : undefined,
          unifiedLiquidityIndicator: ({ data, context }) => {
            if (!(data instanceof Order)) {
              return undefined;
            }

            const groups = getUnifiedLiquidityMarketTooltipGroupsFromEntityMarkets({
              markets: data.Markets,
              legSummary: data.LegSummary,
              marketAccountsByName: context.current.marketAccountsByName ?? new Map(),
              marketDisplayNameByName: context.current.marketDisplayNameByName ?? new Map(),
              securitiesBySymbol: context.current.securitiesBySymbol ?? new Map(),
            });
            if (!shouldShowEntityUnifiedLiquidityMarketTooltip(data, groups)) {
              return undefined;
            }

            return (
              <UnifiedLiquidityTooltipIndicator
                baseSymbol={data.Symbol}
                groups={groups}
                dataTestId="order-blotter-unified-liquidity-tooltip"
                statusTestIdPrefix="order-blotter-unified-liquidity-tooltip-status"
              />
            );
          },
          tours: [SecurityToursEnum.DDH_ENABLED_ORDER],
        },
      },
      {
        field: ORDER_COLUMN_IDS.ExpressedOutcome,
        type: 'text',
        title: 'Outcome',
        initialHide: true,
        sortable: true,
      },
      { field: ORDER_COLUMN_IDS.Side, type: 'side' },
      {
        field: ORDER_COLUMN_IDS.OrderQty,
        type: 'size',
        sortable: true,
        initialWidth: 120,
        params: { currencyField: 'Currency' } satisfies SizeColumnParams,
      },
      {
        field: ORDER_COLUMN_IDS.Price,
        type: 'price',
        sortable: true,
        params: {
          assetField: ORDER_COLUMN_IDS.Symbol,
          showReferencePrice: true,
          showPriceAsRange: true,
          /* The "Price" field is only defined in BPS space for the BPS pricing mode specifically */
          isReferencePrice: (data: Order | undefined) => data?.PricingMode === PricingModeEnum.BPS,
        } satisfies PriceParams<Order>,
      },
      {
        field: ORDER_COLUMN_IDS.PricingReference,
        type: 'price',
        initialHide: true,
        params: {
          isReferencePrice: true,
        },
      },
      { field: ORDER_COLUMN_IDS.Strategy, type: 'strategy', sortable: true },
      {
        field: ORDER_COLUMN_IDS.CumQty,
        type: 'size',
        sortable: true,
        title: 'Filled Qty',
        params: { currencyField: 'Currency' },
        initialWidth: 120,
      },
      {
        id: ORDER_COLUMN_IDS.FilledPercent,
        type: 'filledPercent',
        title: 'Filled %',
        sortable: true,
        params: {
          filledQtyField: ORDER_COLUMN_IDS.CumQty,
          totalQtyField: ORDER_COLUMN_IDS.OrderQty,
          getColor: (node, theme) => getOrderStatusColor(node.data, false, theme),
          tooltip: getMeterTooltip,
        } satisfies FilledPercentColumnParams<Order>,
        // For DDH tours (2.49), we apply a class to the filled % cell. This cell's class is the anchor of the tour.
        cellClass: (params: CellClassParams<Order>) => (params.node.data ? getMeterClassName(params.node.data) : ''),
      },
      {
        id: ORDER_COLUMN_IDS.OrderAnalytics,
        type: 'custom',
        title: 'Analytics',
        params: {
          headerName: '',
          cellRenderer: OrderAnalyticsTooltipCellRenderer,
        },
        sortable: false,
        initialWidth: 80,
      },
      {
        field: ORDER_COLUMN_IDS.FilledPx,
        type: 'price',
        sortable: true,
        title: 'Filled Price',
        params: { assetField: ORDER_COLUMN_IDS.Symbol },
      },
      {
        field: ORDER_COLUMN_IDS.CumFee,
        type: 'size',
        sortable: true,
        title: 'Fees',
        params: { currencyField: 'FeeCurrency' },
        initialWidth: 80,
      },
      { field: ORDER_COLUMN_IDS.OrdStatus, type: 'orderStatus', sortable: true, title: 'Order Status' },
      {
        field: ORDER_COLUMN_IDS.HedgeOrderStatus,
        type: 'autohedgeStatus',
        title: 'Hedge Order Status',
        initialHide: true,
        params: {
          textField: 'HedgeOrderStatus.Text',
        },
      },
      {
        field: ORDER_COLUMN_IDS.UnhedgedQty,
        type: 'size',
        title: 'Unhedged Qty',
        initialHide: true,
        sortable: true,
        params: { currencyField: 'HedgeOrderStatus.Currency' },
      },
      {
        field: ORDER_COLUMN_IDS.SelectedMarkets,
        title: 'Selected Markets',
        type: 'markets',
        sortable: true,
      },
      { field: ORDER_COLUMN_IDS.TradedMarkets, type: 'markets', title: 'Traded Markets', sortable: true },
      {
        id: ORDER_COLUMN_IDS.SubAccount,
        field: ORDER_COLUMN_IDS.SubAccounts,
        type: 'subAccount',
        sortable: true,
        title: 'Sub Account(s)',
        params: {
          allocationCurrencyField: 'Currency',
          allocationField: 'Allocation',
        } satisfies SubAccountColumnParams<Order>,
      },
      { field: ORDER_COLUMN_IDS.User, type: 'user', sortable: true, initialWidth: 140 },
      { field: ORDER_COLUMN_IDS.OrderID, type: 'id' },
      {
        field: ORDER_COLUMN_IDS.SelectedAndTradedMarketAccounts,
        type: 'marketAccounts',
        title: 'Market Accounts',
        initialHide: true,
      },
      {
        field: ORDER_COLUMN_IDS.AvgPx,
        type: 'price',
        sortable: true,
        initialHide: true,
        title: 'Filled Price (no fees)',
        params: { assetField: ORDER_COLUMN_IDS.Symbol, showFeeIcon: 'never' },
      },
      { field: ORDER_COLUMN_IDS.RiskStatus, type: 'bitmap', initialHide: true },
      {
        field: ORDER_COLUMN_IDS.AvgPxAllIn,
        type: 'price',
        sortable: true,
        initialHide: true,
        title: 'Filled Price (with fees)',
        params: { assetField: ORDER_COLUMN_IDS.Symbol },
      },
      {
        field: ORDER_COLUMN_IDS.CumAmt,
        type: 'size',
        sortable: true,
        initialHide: true,
        params: { currencyField: 'AmountCurrency' },
      },
      { field: ORDER_COLUMN_IDS.SelectedAndTradedMarkets, type: 'markets', title: 'Markets', initialHide: true },
      { field: ORDER_COLUMN_IDS.SelectedMarketAccounts, type: 'marketAccounts', sortable: false, initialHide: true },
      { field: ORDER_COLUMN_IDS.TradedMarketAccounts, type: 'marketAccounts', sortable: false, initialHide: true },
      {
        id: ORDER_COLUMN_IDS.FilledNotional,
        type: 'filledNotional',
        sortable: true,
        initialHide: true,
        title: 'Filled Notional',
        params: { securityField: ORDER_COLUMN_IDS.Symbol, showFeeIcon: 'never' },
      },
      {
        id: ORDER_COLUMN_IDS.OpenNotional,
        type: 'openNotional',
        sortable: true,
        initialHide: true,
        title: 'Open Notional',
        params: { securityField: ORDER_COLUMN_IDS.Symbol, showFeeIcon: 'never' },
      },
      {
        id: ORDER_COLUMN_IDS.TotalNotional,
        type: 'totalNotional',
        sortable: true,
        initialHide: true,
        title: 'Total Notional',
        params: { securityField: ORDER_COLUMN_IDS.Symbol, showFeeIcon: 'never' },
      },
      {
        id: ORDER_COLUMN_IDS.FilledCounterAmount,
        type: 'filledCounterAmount',
        sortable: true,
        initialHide: true,
        title: 'Filled Counter Amt',
        params: { currencyField: 'AmountCurrency', showFeeIcon: 'never' },
      },

      { field: ORDER_COLUMN_IDS.Group, type: 'text', sortable: true, initialHide: true },

      { id: ORDER_COLUMN_IDS.ProductType, type: 'productType', sortable: true, initialHide: true, title: 'Product' },
      {
        id: ORDER_COLUMN_IDS.BaseCurrency,
        type: 'currency',
        sortable: true,
        initialHide: true,
        title: 'Base Currency',
        params: { symbolField: 'Symbol', securityCurrencyField: 'BaseCurrency', showDescription: false },
      },
      {
        id: ORDER_COLUMN_IDS.QuoteCurrency,
        type: 'currency',
        sortable: true,
        initialHide: true,
        title: 'Quote Currency',
        params: { symbolField: 'Symbol', securityCurrencyField: 'QuoteCurrency', showDescription: false },
      },

      { field: ORDER_COLUMN_IDS.EndTime, type: 'date', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.LastTradeTime, type: 'date', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.StartTime, type: 'date', sortable: true, initialHide: true },
      {
        field: ORDER_COLUMN_IDS.Timestamp,
        type: 'date',
        sortable: true,
        initialHide: true,
        title: 'Updated at',
        params: { milliseconds: true },
      },
      { id: ORDER_COLUMN_IDS.BaseQty, type: 'cumBaseQty', sortable: true, initialHide: true, title: 'Filled Base Qty' },
      { field: ORDER_COLUMN_IDS.OrdType, type: 'text', sortable: true, initialHide: true, title: 'Order Type' },

      { field: ORDER_COLUMN_IDS.ClOrdID, type: 'id', initialHide: true },
      {
        field: ORDER_COLUMN_IDS.LeavesQty,
        type: 'size',
        sortable: true,
        initialHide: true,
        params: { currencyField: 'Currency' },
        title: 'Open Qty',
      },
      {
        field: ORDER_COLUMN_IDS.RemainQty,
        type: 'size',
        sortable: true,
        initialHide: true,
        params: { currencyField: 'Currency' },
      },
      { field: ORDER_COLUMN_IDS.TimeInForce, type: 'text', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.Comments, type: 'text', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.DecisionStatus, type: 'text', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.ExecID, type: 'text', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.TransactTime, type: 'date', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.Text, type: 'text', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.Parameters, type: 'orderParameters', initialHide: true },
      {
        field: ORDER_COLUMN_IDS.ExpectedFillPrice,
        type: 'price',
        sortable: true,
        initialHide: true,
        params: { assetField: ORDER_COLUMN_IDS.Symbol, showFeeIcon: 'never' },
      },
      {
        field: ORDER_COLUMN_IDS.ExpectedFillQty,
        type: 'size',
        sortable: true,
        initialHide: true,
        params: { currencyField: 'Currency' },
      },
      { field: ORDER_COLUMN_IDS.ParentOrderID, type: 'text', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.Revision, type: 'text', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.OrigClOrdID, type: 'text', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.SessionID, type: 'text', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.Allocation, type: 'text', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.CxlRejReason, type: 'text', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.AggressorSide, type: 'side', sortable: true, initialHide: true },
      {
        field: ORDER_COLUMN_IDS.AllowedSlippageBPS,
        title: 'Allowed Slippage',
        type: 'number',
        sortable: true,
        initialHide: true,
        params: { currency: 'BPS' },
      },
      {
        type: 'custom',
        params: { valueFormatter: ({ value }) => prettyPriceProtection(value) },
        field: ORDER_COLUMN_IDS.PriceProtection,
        initialHide: true,
        sortable: true,
      },
      { field: ORDER_COLUMN_IDS.ParentRFQID, type: 'id', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.QuoteID, type: 'id', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.RFQID, type: 'id', sortable: true, initialHide: true },
      { field: ORDER_COLUMN_IDS.UnifiedLiquidity, type: 'text', initialHide: true, sortable: true },
      { field: ORDER_COLUMN_IDS.ReduceOnly, type: 'text', initialHide: true, sortable: true },
      { field: ORDER_COLUMN_IDS.ReduceFirst, type: 'text', initialHide: true, sortable: true },
      {
        field: ORDER_COLUMN_IDS.FixingIndices,
        title: 'Fixing Index',
        type: 'fixingIndices',
        id: ORDER_COLUMN_IDS.FixingIndices,
        initialHide: true,
      },
      {
        field: ORDER_COLUMN_IDS.FixingDetails,
        title: 'Fixing Price',
        type: 'fixingPrice',
        id: ORDER_COLUMN_IDS.FixingPrice,
        initialHide: true,
      },
      { field: ORDER_COLUMN_IDS.PricingMode, type: 'text', title: 'Pricing Mode', initialHide: true },
      {
        field: ORDER_COLUMN_IDS.IsArchived,
        type: 'text',
        title: 'Archived',
        initialHide: true,
        params: { getLabel: val => (val ? 'Y' : 'N') } satisfies TextColumnParams,
      },
      {
        field: ORDER_COLUMN_IDS.TriggerPrice,
        title: 'Trigger Price',
        type: 'price',
        initialHide: true,
        params: {
          assetField: ORDER_COLUMN_IDS.Symbol satisfies Leaves<Order>,
          showReferencePrice: true,
        } satisfies PriceParams,
      },
      {
        field: ORDER_COLUMN_IDS.RequestSource,
        title: 'Request Source',
        type: 'text',
        initialHide: true,
      },
    ] satisfies ColumnDef<Order>[]
  ).map(c => [getAgGridColId(c), c])
);

const CUSTOMER_ORDER_COLUMNS: Map<string, ColumnDef<Order>> = new Map(
  (
    [
      {
        field: ORDER_COLUMN_IDS.CustomerOrder, // a.k.a Customer Order Counterparty
        title: 'Cust Order',
        type: 'custom',
        sortable: true,
        headerGroupToggle: true,
        columnsInGroup: [
          ORDER_COLUMN_IDS.CustomerOrderID,
          ORDER_COLUMN_IDS.CustomerAccount,
          ORDER_COLUMN_IDS.CustomerQuantity,
          ORDER_COLUMN_IDS.CustomerFilledQuantity,
          ORDER_COLUMN_IDS.CustomerPrice,
          ORDER_COLUMN_IDS.CustomerAvgPx,
          ORDER_COLUMN_IDS.CustomerFilledCumAmt,
        ],
        columnGroup: true,
        initialWidth: 120,
        minWidth: 100,
        params: {
          valueFormatter: ({ value, context, data }: ValueFormatterParams<Order>) => {
            const customerOrderCounterparty = context.current?.customersByName?.get(value)?.DisplayName || value;
            if (!customerOrderCounterparty && data?.ParentOrderID && data.RequestSource === 'Customer') {
              // Show that there is an associated customer order (ParentOrderID) even if we haven't explicitly loaded it yet
              // Should be rare, but we can look at rendering this as an icon with a tooltip instead if it ends up being common
              return 'Customer order';
            }
            return customerOrderCounterparty;
          },
        },
      },
      {
        field: ORDER_COLUMN_IDS.CustomerOrderID,
        title: 'Cust Order ID',
        type: 'id',
        columnGroup: true,
        initialWidth: 100,
        minWidth: 80,
      },
      {
        field: ORDER_COLUMN_IDS.CustomerAccount,
        title: 'Cust Account',
        type: 'marketAccountSourceAccountID',
        sortable: true,
        columnGroup: true,
        initialWidth: 100,
        minWidth: 80,
      },
      {
        field: ORDER_COLUMN_IDS.CustomerQuantity,
        title: 'Cust Qty',
        type: 'size',
        sortable: true,
        params: { currencyField: 'Currency' },
        initialWidth: 100,
        minWidth: 80,
        columnGroup: true,
      },
      {
        field: ORDER_COLUMN_IDS.CustomerFilledQuantity,
        title: 'Cust Filled Qty',
        type: 'size',
        sortable: true,
        params: { currencyField: 'Currency' },
        initialWidth: 120,
        minWidth: 80,
        columnGroup: true,
      },
      {
        field: ORDER_COLUMN_IDS.CustomerPrice,
        title: 'Cust Limit Price',
        type: 'price',
        sortable: true,
        params: { assetField: ORDER_COLUMN_IDS.Symbol },
        initialWidth: 120,
        minWidth: 80,
        columnGroup: true,
      },
      {
        field: ORDER_COLUMN_IDS.CustomerAvgPx,
        title: 'Cust Avg. Filled Price',
        type: 'price',
        sortable: true,
        params: { assetField: ORDER_COLUMN_IDS.Symbol },
        initialWidth: 160,
        minWidth: 80,
        columnGroup: true,
      },
      {
        field: ORDER_COLUMN_IDS.CustomerFilledCumAmt,
        title: 'Cust Filled Counter Amt',
        type: 'price',
        sortable: true,
        params: { quoteCurrencyField: 'customerAmountCurrency' },
        initialWidth: 160,
        minWidth: 80,
        columnGroup: true,
      },
    ] satisfies ColumnDef<Order>[]
  ).map(c => [getAgGridColId(c), c])
);

export const useOrderColumns = ({
  defaultColumns = EMPTY_ARRAY,
  suppressCustomerColumns,
  additionalColumns = EMPTY_ARRAY,
}: {
  defaultColumns?: OrderBlotterDefaultColumn[];
  suppressCustomerColumns?: boolean; // Skip showing Customer columns
  additionalColumns?: Column[];
}) => {
  const { enableCustomerBlotterColumns } = useTradingSettings();
  const { enablePredictionMarkets, enableSEDOLDisplay } = useFeatureFlag();

  const columnDefinitions = useMemo(() => {
    const definitionsMap = new Map<string, ColumnDef<Order>>(DEFAULT_ORDER_COLUMNS.entries());
    if (enableCustomerBlotterColumns && !suppressCustomerColumns) {
      definitionsMap.clear();
      const cols = [...DEFAULT_ORDER_COLUMNS.values()]
        .slice(0, 2)
        .concat([...CUSTOMER_ORDER_COLUMNS.values()])
        .concat([...DEFAULT_ORDER_COLUMNS.values()].slice(2));
      cols.forEach(col => {
        definitionsMap.set(getAgGridColId(col), col);
      });
    }

    if (!enablePredictionMarkets) {
      definitionsMap.delete(ORDER_COLUMN_IDS.ExpressedOutcome);
    }

    const columnsById = definitionsMap as Map<string, Column>;
    const symbolColumn = columnsById.get(ORDER_COLUMN_IDS.Symbol);
    if (symbolColumn != null) {
      columnsById.set(ORDER_COLUMN_IDS.Symbol, {
        ...symbolColumn,
        getQuickFilterText: createGetSecurityIdentifierColumnQuickFilterText(enableSEDOLDisplay),
      });
    }

    for (const column of getSecurityIdentifierColumns({
      symbolField: ORDER_COLUMN_IDS.Symbol,
      includeSedol: enableSEDOLDisplay,
    })) {
      columnsById.set(getAgGridColId(column), column);
    }
    if (additionalColumns.length === 0) {
      return columnsById;
    }

    const withAdditionalColumns = new Map(columnsById);
    for (const column of additionalColumns) {
      withAdditionalColumns.set(getAgGridColId(column), column);
    }
    return withAdditionalColumns;
  }, [
    additionalColumns,
    enableCustomerBlotterColumns,
    enablePredictionMarkets,
    enableSEDOLDisplay,
    suppressCustomerColumns,
  ]);

  return useDefaultColumns(defaultColumns, columnDefinitions);
};
